import styled from '@emotion/styled'
import React, { forwardRef } from 'react'
import { useTranslation } from 'react-i18next'

import { Text } from '../..//Typography/Typography'
import { scale } from '../../../utils/scale'
import { ColumnFlex } from '../../Layout/Layout'
import { groupItems } from '../utils'

import SearchItem from './SearchItem'

const SearchItemGroup = styled(ColumnFlex)`
  margin-bottom: 12px;

  .MuiButton-root {
    margin-top: 16px;
  }
`

const SearchItemGroupTitle = styled(Text)`
  padding: 0 16px;
  margin-top: ${scale(3)};
  margin-bottom: ${scale(2)};
`

type SearchItem = {
  title: string
  onClick: () => void
  startIcon?: JSX.Element
  endIcon?: boolean
  description?: string
  group?: string
}

type Props = {
  items: SearchItem[]
  searchWord?: string
}

const ResultsFound = forwardRef<HTMLHeadElement, Props>(({ items, searchWord }, ref) => {
  const { t } = useTranslation()
  const noGroupId = t('component.search.group.none')
  const itemsGrouped = groupItems(items, noGroupId)
  const groupKeys = Object.keys(itemsGrouped)
  const showGroups =
    groupKeys.length > 1 || (groupKeys.length === 1 && !groupKeys.includes(noGroupId))

  const refQueue = [ref]

  return (
    <ColumnFlex role="list">
      {Object.entries(itemsGrouped).map(([group, groupedItems]) => (
        <SearchItemGroup
          key={`search-item-group-${group}`}
          role="listitem"
          aria-labelledby={`search-result-group-${group}`}
        >
          {showGroups && group !== noGroupId && (
            <SearchItemGroupTitle
              ref={refQueue.pop()}
              tabIndex={-1}
              role="presentation"
              id={`search-result-group-${group}`}
              $size={400}
              $weight="Medium"
              $height="Medium"
            >
              {group}
            </SearchItemGroupTitle>
          )}
          <ul>
            {groupedItems.map((item, index) => (
              <SearchItem
                key={`search-result-${group}-list-block-${index}`}
                title={item.title}
                onClick={item.onClick}
                startIcon={item.startIcon}
                endIcon={item.endIcon}
                description={item.description}
                searchWord={searchWord}
                data-cy="search-result-item"
              />
            ))}
          </ul>
        </SearchItemGroup>
      ))}
    </ColumnFlex>
  )
})

export default ResultsFound
